import { useId, useState } from 'react'
import { cn } from '@/lib/utils'
import { DsFieldFrame, DsFieldShell } from '@/ds/field'
import { DsChip } from '@/ds/chip'
import { currencyLocale, currencySymbol } from '@/ds/currency'

export interface DsQuickAmount {
  label: string
  amount: number
}

export interface DsMoneyInputProps {
  value?: number | null
  onChange: (value: number | null) => void
  label?: string
  helper?: string
  error?: string | null
  required?: boolean
  disabled?: boolean
  hintText?: string
  quickAmounts?: DsQuickAmount[]
  maxDecimals?: number
  maxAmount?: number
  /** Overrides the glyph `currency` gives; kept for callers that predate `currency`. */
  symbol?: string
  /** ISO 4217 code of the record the amount belongs to. Drives the glyph and the grouping; absent means INR. */
  currency?: string | null
  className?: string
}

const groupIN = (intPart: string) => {
  // 12,34,567 — Indian grouping: last three, then twos.
  if (intPart.length <= 3) return intPart
  const last3 = intPart.slice(-3)
  const rest = intPart.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')
  return `${rest},${last3}`
}

// 1,234,567 — every other currency (currencyLocale: grouping follows the money, not the reader).
const groupWestern = (intPart: string) => intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
const grouperFor = (currency?: string | null) => (currencyLocale(currency) === 'en-IN' ? groupIN : groupWestern)

export function formatMoneyInput(raw: string, maxDecimals: number, currency?: string | null): string {
  if (!raw) return ''
  const [i, d] = raw.split('.')
  const dec = d === undefined ? '' : `.${d.slice(0, maxDecimals)}`
  return grouperFor(currency)(i.replace(/^0+(?=\d)/, '')) + dec
}

export function DsMoneyInput({ value, onChange, label, helper, error, required, disabled, hintText = '0', quickAmounts = [], maxDecimals = 2, maxAmount, symbol, currency, className }: DsMoneyInputProps) {
  const id = useId()
  // The table's glyph carries a trailing space for code-style symbols ("AED "); the frame already gaps it.
  const glyph = symbol ?? currencySymbol(currency).trim()
  const group = grouperFor(currency)
  const [focused, setFocused] = useState(false)
  const [raw, setRaw] = useState<string>(value == null ? '' : String(value))
  const [prevProp, setPrevProp] = useState(value)
  if (value !== prevProp) {
    // The parent changed the value (quick amount, reset). Only resync when it differs from what was
    // typed, so an echo of our own onChange does not eat a trailing decimal point.
    setPrevProp(value)
    const typed = raw === '' || raw === '.' ? null : Number(raw)
    if (typed !== value) setRaw(value == null ? '' : String(value))
  }

  const commit = (next: string) => {
    let cleaned = next.replace(/[^\d.]/g, '')
    const firstDot = cleaned.indexOf('.')
    if (firstDot !== -1) cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '').slice(0, maxDecimals)
    if (maxDecimals === 0) cleaned = cleaned.replace(/\..*$/, '')
    if (maxAmount !== undefined && Number(cleaned) > maxAmount) cleaned = String(maxAmount)
    setRaw(cleaned)
    onChange(cleaned === '' || cleaned === '.' ? null : Number(cleaned))
  }

  return (
    <DsFieldShell label={label} required={required} helper={helper} error={error} disabled={disabled} htmlFor={id} className={className}>
      <DsFieldFrame focused={focused} hasError={!!error} disabled={disabled}>
        <span className="text-body font-medium text-muted-foreground" aria-hidden>
          {glyph}
        </span>
        <input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          value={formatMoneyInput(raw, maxDecimals, currency)}
          placeholder={hintText}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || helper ? `${id}-helper` : undefined}
          onChange={(e) => commit(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={cn('min-w-0 flex-1 bg-transparent text-numeric outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed')}
        />
      </DsFieldFrame>
      {quickAmounts.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {quickAmounts.map((q) => (
            <DsChip key={q.label} label={`${q.label} · ${symbol ?? currencySymbol(currency)}${group(String(Math.round(q.amount)))}`} selected={value === q.amount} onClick={() => commit(String(q.amount))} size="sm" />
          ))}
        </div>
      )}
    </DsFieldShell>
  )
}
